import { Box, Text, Card, Flex, SegmentedControl } from '@radix-ui/themes';
import { useSelector } from 'react-redux';
import CircleProgress from '../../components/shared/CircleProgress'
import React from 'react';
import { DateTime } from 'luxon';

const StudentProgressSummary = () => {
  const quizzes = useSelector((state) => state.studentAccount.quizzes);
  const [attemptType, setAttemptType] = React.useState('last'); // 'first' or 'last'

  console.log('quizzes>>>>>>>>>>>>>>>>', quizzes);

  // Sort quizzes by date
  const sortedQuizzes = [...quizzes].sort((a, b) =>
    DateTime.fromISO(a.created_at) < DateTime.fromISO(b.created_at) ? 1 : -1
  );

  // Get time-based metrics
  const now = DateTime.now();
  const yesterday = now.minus({ days: 1 }).startOf('day');
  const yesterdayEnd = yesterday.endOf('day');

  // Filter yesterday's quizzes
  const yesterdayQuizzes = sortedQuizzes.filter(quiz => {
    const quizDate = DateTime.fromISO(quiz.created_at);
    return quizDate >= yesterday && quizDate <= yesterdayEnd;
  });

  const calculateYesterdayStats = () => {
    if (!yesterdayQuizzes.length) return null;

    const stats = {
      total: yesterdayQuizzes.length,
      attempted: 0,
      perfectScore: 0,
      averageScore: 0,
    };

    yesterdayQuizzes.forEach(quiz => {
      const attempt = getRelevantAttempt(quiz);
      if (quiz.quiz_attempts.length > 0) {
        stats.attempted++;
        stats.averageScore += attempt.percentage;
        if (attempt.percentage === 100) {
          stats.perfectScore++;
        }
      }
    });

    stats.averageScore = stats.attempted ? stats.averageScore / stats.attempted : 0;
    return stats;
  };

  const thisWeekStart = now.startOf('week');

  const quizzesByPeriod = {
    thisWeek: sortedQuizzes.filter(quiz =>
      DateTime.fromISO(quiz.created_at) >= thisWeekStart
    ),
    lastWeek: sortedQuizzes.filter(quiz =>
      DateTime.fromISO(quiz.created_at) >= yesterday &&
      DateTime.fromISO(quiz.created_at) < thisWeekStart
    )
  };

  const totalQuizzes = quizzes?.length || 0;

  if (totalQuizzes === 0) {
    return (
      <Box>
        <Card>
          <Text size="2" color="gray">No quizzes available yet</Text>
        </Card>
      </Box>
    );
  }

  // Get the appropriate attempt or return zeros if no attempts
  const getRelevantAttempt = (quiz) => {
    if (quiz.quiz_attempts.length === 0) {
      return {
        percentage: 0,
        vocabulary_score: 0,
        concept_score: 0,
        application_score: 0,
        created_at: quiz.created_at
      };
    }

    if (attemptType === 'first') {
      return quiz.quiz_attempts[0];
    }
    return quiz.quiz_attempts[quiz.quiz_attempts.length - 1];
  };

  // Calculate averages including all quizzes
  const averageScore = quizzes.reduce((acc, quiz) => {
    const attempt = getRelevantAttempt(quiz);
    return acc + attempt.percentage;
  }, 0) / totalQuizzes;

  const averagesByType = {
    vocabulary: quizzes.reduce((acc, quiz) => {
      const attempt = getRelevantAttempt(quiz);
      return acc + attempt.vocabulary_score;
    }, 0) / totalQuizzes,
    concept: quizzes.reduce((acc, quiz) => {
      const attempt = getRelevantAttempt(quiz);
      return acc + attempt.concept_score;
    }, 0) / totalQuizzes,
    application: quizzes.reduce((acc, quiz) => {
      const attempt = getRelevantAttempt(quiz);
      return acc + attempt.application_score;
    }, 0) / totalQuizzes
  };

  const totalAttempted = quizzes.filter(quiz => quiz.quiz_attempts.length > 0).length;

  const calculatePeriodStats = (periodQuizzes) => {
    if (!periodQuizzes.length) return null;

    return {
      averageScore: periodQuizzes.reduce((acc, quiz) => {
        const attempt = getRelevantAttempt(quiz);
        return acc + attempt.percentage;
      }, 0) / periodQuizzes.length,
      totalAttempted: periodQuizzes.filter(quiz => quiz.quiz_attempts.length > 0).length,
      total: periodQuizzes.length
    };
  };

  const thisWeekStats = calculatePeriodStats(quizzesByPeriod.thisWeek);
  const lastWeekStats = calculatePeriodStats(quizzesByPeriod.lastWeek);

  const yesterdayStats = calculateYesterdayStats();

  return (
    <Box>
      <Flex direction="column" gap="4" wrap="wrap" align="center" justify="center">
        <SegmentedControl.Root
          defaultValue="last"
          value={attemptType}
          onValueChange={setAttemptType}
        >
          <SegmentedControl.Item value="first">First Attempts</SegmentedControl.Item>
          <SegmentedControl.Item value="last">Last Attempts</SegmentedControl.Item>
        </SegmentedControl.Root>

        {/* Yesterday's Progress */}
        {yesterdayStats && (
          <>
            <Text size="2" weight="bold">Yesterday's Progress</Text>
            <Flex gap="3" mt="2">
              <Box>
                <Text size="2" color="gray">Completed</Text>
                <Text size="3">{yesterdayStats.attempted}/{yesterdayStats.total}</Text>
              </Box>
              <Box>
                <Text size="2" color="gray">Perfect Score</Text>
                <Text size="3">{yesterdayStats.perfectScore}</Text>
              </Box>
              <Box>
                <Text size="2" color="gray">Average</Text>
                <Text size="3">{Math.round(yesterdayStats.averageScore)}%</Text>
              </Box>
            </Flex>
          </>
        )}

        {/* Weekly Progress */}
        {thisWeekStats && (
          <Card>
            <Text size="2" weight="bold">This Week</Text>
            <Text size="2">Average: {Math.round(thisWeekStats.averageScore)}%</Text>
            <Text size="2">Completed: {thisWeekStats.totalAttempted}/{thisWeekStats.total}</Text>
          </Card>
        )}

        {lastWeekStats && (
          <Card>
            <Text size="2" weight="bold">Last Week</Text>
            <Text size="2">Average: {Math.round(lastWeekStats.averageScore)}%</Text>
            <Text size="2">Completed: {lastWeekStats.totalAttempted}/{lastWeekStats.total}</Text>
          </Card>
        )}

        <Flex direction="row" gap="4" align="center" justify="center">
          <Box align="center" justify="center">
            <CircleProgress progress={averageScore} size={100} strokeWidth={5} label={`${Math.floor(averageScore)}%`} labelSize="4" />
            <Text size="2" color="gray">Average Score</Text>
          </Box>
          <Flex direction="column" align="center" justify="center">
            <CircleProgress progress={totalAttempted * 100 / totalQuizzes} size={80} strokeWidth={5} label={`${Math.floor(totalAttempted / totalQuizzes * 100)}%`} labelSize="4" />
            <Text weight="bold">{totalAttempted}/{totalQuizzes}</Text>
            <Text size="1">quizzes attempted</Text>
          </Flex>
        </Flex>
        <Card>
          <Text size="2" className="pill">Vocabulary: {averagesByType.vocabulary.toFixed(1)}%</Text>
          <Text size="2" className="pill">Concept: {averagesByType.concept.toFixed(1)}%</Text>
          <Text size="2" className="pill">Application: {averagesByType.application.toFixed(1)}%</Text>
        </Card>
      </Flex>
    </Box>
  );
};

export default StudentProgressSummary;
